import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useNewsletterSubscription } from '@/hooks/use-newsletter-subscription';
import { validateEmail, sanitizeEmail } from '@/utils/validation/email';
import { isBrowser } from '@/utils/util_helpers/ssr';

const NewsletterSection = () => {
  const { t } = useTranslation();
  const { subscribeNewsletter, isLoading } = useNewsletterSubscription();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeEmail(e.target.value);
    setEmail(sanitizedValue);

    if (emailError) {
      setEmailError('');
    }

    if (isSuccess) {
      setIsSuccess(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateEmail(email);

    if (!validation.isValid) {
      const errorKey = `js.features.marketplace.newsletter.errors.${validation.error}`;
      setEmailError(t(errorKey));
      return;
    }

    const sanitizedEmail = sanitizeEmail(email);
    setEmailError('');

    try {
      await subscribeNewsletter(sanitizedEmail);

      setIsSuccess(true);
      setEmail('');

      if (isBrowser()) {
        sessionStorage.setItem('subscribed_email', sanitizedEmail);
      }
    } catch (error) {
      const errorMessage = t('js.features.marketplace.newsletter.errors.subscription_failed');
      setEmailError(errorMessage);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className={clsx('bg-bg-primaryAccentLight', 'py-8')}>
      <div
        className={clsx('mx-auto', 'w-[calc(100%-theme(space.7))] sm:w-[80%]', 'max-w-[130rem]')}
      >
        <div className="flex flex-col items-center">
          <div className="w-full">
            <h1
              className={clsx(
                'font-extrabold text-text-primaryHeavy',
                'text-[25px] lg:text-[48px]',
                'leading-[32px] lg:leading-[54px]',
                'mb-2'
              )}
            >
              {t('js.features.marketplace.newsletter.title')}
            </h1>

            <p className={clsx('text-text-primaryHeavy', 'text-2xl lg:text-4xl', 'mb-4')}>
              {t('js.features.marketplace.newsletter.subtitle')}
            </p>

            <form onSubmit={handleSubmit}>
              <div
                className={clsx(
                  'flex',
                  'flex-col sm:flex-row sm:flex-wrap',
                  'gap-2',
                  'mb-2'
                )}
              >
                <div className={clsx('flex-1', 'sm:max-w-[50%]', 'sm:mr-3')}>
                  <input
                    type="email"
                    placeholder={t('js.features.marketplace.newsletter.placeholder')}
                    value={email}
                    onChange={handleEmailChange}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading || isSuccess}
                    className={clsx(
                      'rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2',
                      'w-full',
                      'p-3',
                      'text-2xl',
                      emailError
                        ? 'border-red-500 focus:ring-red-500 border-2'
                        : isSuccess
                        ? 'border-2 border-green-500 focus:ring-green-500'
                        : 'focus:ring-yellow-500',
                      'disabled:cursor-not-allowed disabled:opacity-60'
                    )}
                    aria-label={t('js.features.marketplace.newsletter.aria_label')}
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'email-error' : undefined}
                    maxLength={254}
                    autoComplete="email"
                  />
                  {emailError && (
                    <p id="email-error" className={clsx('text-red-600', 'text-base', 'mt-2')}>
                      {emailError}
                    </p>
                  )}
                  {isSuccess && (
                    <p className={clsx('text-green-600', 'text-base', 'mt-2')}>
                      {t('js.features.marketplace.newsletter.success')}
                    </p>
                  )}
                </div>
                <p
                  className={clsx(
                    'font-medium text-text-primaryMedium',
                    'text-xl lg:text-body',
                    'mb-3 sm:mb-0',
                    'sm:order-last sm:basis-full'
                  )}
                >
                  {t('js.features.marketplace.newsletter.disclaimer')}
                  <br />
                  {t('js.features.marketplace.newsletter.disclaimer_unsubscribe')}
                </p>
                <button
                  type="submit"
                  disabled={isLoading || !email || isSuccess}
                  className={clsx(
                    'self-start rounded transition-all',
                    'p-3',
                    'font-semibold text-text-primaryHeavy',
                    'text-3xl',
                    'bg-bg-primaryAccentHeavy',
                    'shadow-[0_5px_30px_0_rgba(254,206,31,0.24)]',
                    'hover:bg-fill-button-primaryMedium',
                    'active:bg-text-yellow',
                    'disabled:cursor-not-allowed disabled:bg-fill-button-disabled disabled:opacity-60 disabled:shadow-none',
                    'order-last sm:order-none'
                  )}
                  aria-label={t('js.features.marketplace.newsletter.button_aria_label')}
                >
                  {isLoading
                    ? t('js.features.marketplace.newsletter.button_subscribing')
                    : isSuccess
                    ? t('js.features.marketplace.newsletter.button_subscribed')
                    : t('js.features.marketplace.newsletter.button_subscribe')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export { NewsletterSection };
